import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import Confetti from '../../components/Confetti';
import Icon from '../../components/Icon';
import OutlinedCircle from '../../components/OutlinedCircle';
import EventTicket, { PHONE_GUTTER } from './EventTicket';
import { withAlpha } from '../../shared/utils/color';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { HIDE_MS, SWIPE_CONFIRM_MS, SWIPE_FADE_MS } from '../../shared/utils/constants';
import { type Opportunity } from '../../shared/opportunity';

/**
 * One event, on a phone: `EventTicket` and the push that moves it between lists.
 *
 * The ticket itself — title, time, neighborhood, summary, intent icons and the
 * topic notes down its right edge — is drawn by `EventTicket`, which a shared
 * schedule uses too. What is here is the gesture.
 *
 * There are no buttons. On a phone you do not aim at a 20px target to save
 * something — you push the whole card. Right adds it to My Schedule, left saves
 * it for later. A band of colour grows behind the card as it goes, the action is
 * named a third of the way across, and letting go past halfway finishes the
 * push. The band then says it is done for a few seconds before the list closes
 * the gap. Under the Saved pill the same push moves an event to the other list
 * or takes it off the one it is on, and a mark in the corner says which list
 * holds it. A tap anywhere opens the event.
 */

/** Between one ticket and the next. The band covers it above and below its card. */
const CARD_GAP = 23;

/** Below this a movement is still a tap, and the card must not slide at all. */
const SLOP_PX = 8;
/** How far across the screen the card goes before the action is named. */
const LABEL_AT = 1 / 3;
const LABEL_FADE_MS = 500;
/** Going the other way it clears quickly, before the card is pushed back past it. */
const LABEL_HIDE_MS = 200;
/** The most a released card takes to finish leaving; less when it is nearly gone. */
const EXIT_MS = 320;
const SNAP_MS = 260;
/** A card still listed after its confirmation, sliding back in. */
const RETURN_MS = 380;

/** Green to schedule, purple to save, red to take an event off either list. */
const BAND_COLOR = { schedule: '#698f3f', save: '#9570bd', unschedule: '#8f3f3f', unsave: '#8f3f3f' } as const;

type Side = 'left' | 'right';
type Action = 'schedule' | 'unschedule' | 'save' | 'unsave';
type Phase = 'rest' | 'drag' | 'exit' | 'done' | 'fade' | 'return';

/**
 * Clear where the card is, full colour at the screen edge. The colour comes in
 * with the square of the distance, so most of the band stays close to the page
 * and it deepens only near the edge.
 */
function bandGradient(action: Action): string {
  const side = action === 'schedule' || action === 'unschedule' ? 'right' : 'left';
  const stops = Array.from({ length: 9 }, (_, i) => {
    const t = i / 8;
    return `${withAlpha(BAND_COLOR[action], Number((t * t).toFixed(3)))} ${t * 100}%`;
  });
  return `linear-gradient(to ${side}, ${stops.join(', ')})`;
}

const BAND: Record<Action, string> = {
  schedule: bandGradient('schedule'),
  unschedule: bandGradient('unschedule'),
  save: bandGradient('save'),
  unsave: bandGradient('unsave'),
};

/** [while swiping, once done, second line] */
const WORDS: Record<Action, [string, string, string]> = {
  schedule: ['Mark', 'Marked', 'Going'],
  unschedule: ['Clear', 'Cleared', 'Going'],
  save: ['Mark', 'Marked', 'Maybe'],
  unsave: ['Clear', 'Cleared', 'Maybe'],
};

/** The screen's full width (the list places it so), for the band and the card to reach both edges. */
const Row = styled('div')({
  position: 'relative',
  marginBottom: CARD_GAP,
  padding: `0 ${PHONE_GUTTER}px`,
  // Nothing past the screen's edges, while the band still reaches over the gaps above and below.
  overflowX: 'clip',
  // Vertical panning stays with the page; the horizontal axis is ours.
  touchAction: 'pan-y',
});

/**
 * The full width, scaled down toward the edge it grows from: a transform the
 * compositor draws rather than a width repainted every frame, which could leave
 * a hairline of colour at the screen's edge once the band had closed.
 */
const Band = styled('div')({
  position: 'absolute',
  top: -CARD_GAP,
  bottom: -CARD_GAP,
  left: 0,
  right: 0,
  pointerEvents: 'none',
});

/** "Add to Schedule": centred between the card's trailing edge and the screen's. */
const Label = styled('div')({
  position: 'absolute',
  top: '50%',
  left: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  fontFamily: FONTS.body,
  fontSize: 17.5,
  lineHeight: '21px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: BRAND.parchment,
  pointerEvents: 'none',
});

/** The ticket and its notes, which travel together. */
const Slide = styled('div')({
  position: 'relative',
  cursor: 'pointer',
  userSelect: 'none',
  WebkitUserSelect: 'none',
});

interface Props {
  event: Opportunity;
  rank: number;
  scheduled: boolean;
  saved: boolean;
  /** Listed under the Saved pill, where adding to one list moves the event rather than removes it. */
  savedView: boolean;
  onOpen: (event: Opportunity, rank: number) => void;
  onToggleSchedule: (id: string) => void;
  onToggleSave: (id: string) => void;
  /** Set on one card at a time to demonstrate the gesture. */
  hint?: Side | null;
  hidden?: boolean;
  past?: boolean;
  /** False for a cancelled or full event: the push toward Going gives a little and springs back. */
  canGo?: boolean;
  /** Under 18 is on and the listing states no age. */
  ageWarn?: boolean;
  /** Later dates of the same series that also match. */
  moreDates?: number;
  /** Travel time from the origin, after the neighborhood. */
  travel?: ReactNode;
}

function MobileEventCardBase({
  event,
  rank,
  scheduled,
  saved,
  savedView,
  onOpen,
  onToggleSchedule,
  onToggleSave,
  hint = null,
  hidden = false,
  past = false,
  canGo = true,
  ageWarn = false,
  moreDates = 0,
  travel,
}: Props) {
  const [dx, setDx] = useState(0);
  const [phase, setPhase] = useState<Phase>('rest');
  /** The row's width, read when a push starts: the screen's, on a phone. */
  const [width, setWidth] = useState(0);
  /** The last side pushed toward, kept so the band can shrink back on that side. */
  const [side, setSide] = useState<Side>('right');
  /** What a finished push did, fixed at release: the lists change under it. */
  const [committed, setCommitted] = useState<Action | null>(null);
  const [exitMs, setExitMs] = useState(EXIT_MS);
  const [burst, setBurst] = useState(0);

  /** Set once a gesture is known to be horizontal, so a scroll is never one. */
  const claimed = useRef(false);
  const row = useRef<HTMLDivElement | null>(null);
  const timers = useRef<number[]>([]);
  const latest = useRef({ id: event.id, scheduled, saved, savedView, onToggleSchedule, onToggleSave, canGo });
  useEffect(() => {
    latest.current = { id: event.id, scheduled, saved, savedView, onToggleSchedule, onToggleSave, canGo };
  });

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  const later = useCallback((ms: number, run: () => void) => {
    timers.current.push(window.setTimeout(run, ms));
  }, []);

  if (hint && phase === 'rest' && hint !== side) setSide(hint);

  useLayoutEffect(() => {
    if (hint && row.current) setWidth(row.current.getBoundingClientRect().width);
  }, [hint]);

  /**
   * Past halfway, letting go finishes the push: the card carries on off the
   * screen, and only once it has gone does the list change and the band say so.
   */
  const commit = useCallback(
    (toward: Side, from: number, across: number) => {
      const now = latest.current;
      const action: Action =
        toward === 'right'
          ? now.scheduled ? 'unschedule' : 'schedule'
          : now.saved ? 'unsave' : 'save';
      // RSVP'd events stay in the list, so the card comes back; only under the
      // Saved pill does clearing its status take it off the list.
      const stays = !(now.savedView && (action === 'unschedule' || action === 'unsave'));
      const ms = Math.round(EXIT_MS * Math.max(0.4, 1 - Math.abs(from) / across));

      setCommitted(action);
      setExitMs(ms);
      setPhase('exit');
      setDx(toward === 'right' ? across : -across);

      later(ms, () => {
        setPhase('done');
        if (action === 'schedule') setBurst((n) => n + 1);
        const { id, onToggleSchedule: schedule, onToggleSave: save } = latest.current;
        if (toward === 'right') schedule(id);
        else save(id);
      });
      later(ms + SWIPE_CONFIRM_MS, () => setPhase('fade'));
      // A card that leaves has had its row closed well before this. One that is
      // somehow still here comes back rather than leave a gap in the list.
      later(ms + SWIPE_CONFIRM_MS + SWIPE_FADE_MS + (stays ? 0 : HIDE_MS + 1000), () => {
        setPhase('return');
        setDx(0);
        later(RETURN_MS, () => {
          setPhase('rest');
          setCommitted(null);
        });
      });
    },
    [later],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (phase !== 'rest') return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const startX = e.clientX;
      const startY = e.clientY;
      const id = e.pointerId;
      const across = row.current?.getBoundingClientRect().width ?? window.innerWidth;
      claimed.current = false;

      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== id) return;
        const moveX = ev.clientX - startX;
        const moveY = ev.clientY - startY;

        if (!claimed.current) {
          // The first few pixels decide who owns the gesture. Down the page is
          // the list's, across is the card's, and a diagonal belongs to
          // whichever it is more of.
          if (Math.abs(moveY) > SLOP_PX && Math.abs(moveY) >= Math.abs(moveX)) {
            detach();
            return;
          }
          if (Math.abs(moveX) < SLOP_PX) return;
          claimed.current = true;
          setWidth(across);
          setPhase('drag');
        }

        ev.preventDefault();
        // Finger for finger: the band grows at the card's own rate. Toward
        // Going on an event you can't go to, it gives a little and no more.
        if (moveX !== 0) setSide(moveX > 0 ? 'right' : 'left');
        const blocked = !latest.current.canGo && !latest.current.scheduled;
        setDx(Math.max(-across, Math.min(blocked ? across * 0.12 : across, moveX)));
      };

      const detach = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', cancel);
      };

      const settle = () => {
        setPhase('rest');
        setDx(0);
      };

      const cancel = (ev: PointerEvent) => {
        if (ev.pointerId !== id) return;
        detach();
        if (claimed.current) settle();
      };

      const end = (ev: PointerEvent) => {
        if (ev.pointerId !== id) return;
        detach();
        if (!claimed.current) return;
        const moveX = Math.max(-across, Math.min(across, ev.clientX - startX));
        // Halfway off the screen: the ticket is centred, so that is when its
        // middle crosses the edge, half the screen's width from where it began.
        const blocked = moveX > 0 && !latest.current.canGo && !latest.current.scheduled;
        if (!blocked && Math.abs(moveX) >= across / 2) commit(moveX > 0 ? 'right' : 'left', moveX, across);
        else settle();
      };

      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', cancel);
    },
    [phase, commit],
  );

  const hintShift = hint ? (hint === 'right' ? 1 : -1) * (Math.round(width * LABEL_AT) + 20) : 0;
  const shift = phase === 'rest' && hint ? hintShift : dx;

  const motion =
    phase === 'drag'
      ? '0ms linear'
      : phase === 'exit'
        ? `${exitMs}ms cubic-bezier(0.2, 0.7, 0.3, 1)`
        : `${phase === 'return' ? RETURN_MS : SNAP_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;

  const action: Action =
    committed ?? (side === 'right' ? (scheduled ? 'unschedule' : 'schedule') : saved ? 'unsave' : 'save');
  const finished = phase === 'done' || phase === 'fade';
  const gone = phase === 'fade' || phase === 'return';
  const labelShown = phase === 'done' || (!gone && width > 0 && Math.abs(shift) >= width * LABEL_AT);
  const labelMs = gone ? SWIPE_FADE_MS : labelShown ? LABEL_FADE_MS : LABEL_HIDE_MS;

  // Hidden outright whenever it is not in use, once whatever it was doing has
  // finished: closing behind a card that sprang back, or fading after it said done.
  const bandActive = phase === 'drag' || phase === 'exit' || phase === 'done' || (phase === 'rest' && shift !== 0);
  const bandHideMs = phase === 'fade' ? SWIPE_FADE_MS : phase === 'return' ? 0 : SNAP_MS;
  const bandScale = width > 0 ? Math.min(Math.abs(shift) / width, 1) : 0;

  // The trailing edge of the card, clamped to the screen, and the label halfway
  // between it and the screen edge the card is heading for.
  const trailing = Math.max(0, Math.min(width, side === 'right' ? PHONE_GUTTER + shift : width - PHONE_GUTTER + shift));
  const labelX = side === 'right' ? trailing / 2 : (trailing + width) / 2;
  const [before, after, second] = WORDS[action];

  return (
    <Row ref={row}>
      {/* Always drawn, at no width and no opacity when still, so that a card
          springing back takes its band and label with it. */}
      <Band
        aria-hidden="true"
        style={{
          transformOrigin: side === 'right' ? '100% 50%' : '0 50%',
          transform: `scaleX(${bandScale})`,
          background: BAND[action],
          opacity: gone ? 0 : 1,
          visibility: bandActive ? 'visible' : 'hidden',
          transition: `transform ${motion}, opacity ${SWIPE_FADE_MS}ms ease, visibility 0s linear ${bandActive ? 0 : bandHideMs}ms`,
        }}
      />
      <Label
        aria-hidden={!finished}
        role={finished ? 'status' : undefined}
        style={{
          transform: `translate(${labelX}px, -50%) translateX(-50%)`,
          opacity: labelShown ? 1 : 0,
          visibility: labelShown ? 'visible' : 'hidden',
          transition: `transform ${motion}, opacity ${labelMs}ms ease, visibility 0s linear ${labelShown ? 0 : labelMs}ms`,
        }}
      >
        <Confetti trigger={burst} pieces={18} reach={46} size={5} durationMs={1000} />
        <LabelIcon action={action} finished={finished} />
        <span>
          {finished ? after : before}
          <br />
          {second}
        </span>
      </Label>

      <Slide
        // The card's own swipe; the page's swipes start anywhere else.
        data-own-swipe
        onPointerDown={onPointerDown}
        onClick={() => {
          if (claimed.current || phase !== 'rest') return;
          onOpen(event, rank);
        }}
        style={{ transform: `translateX(${shift}px)`, transition: `transform ${motion}` }}
      >
        <EventTicket
          event={event}
          saved={saved}
          scheduled={scheduled}
          past={past}
          hidden={hidden}
          ageWarn={ageWarn}
          moreDates={moreDates}
          travel={travel}
        />
      </Slide>
    </Row>
  );
}

/** Outlined while the push is still a proposal, filled once it has happened. */
function LabelIcon({ action, finished }: { action: Action; finished: boolean }) {
  switch (action) {
    case 'schedule':
      return finished ? <Icon name="ui:scheduleOn" size={25} /> : <OutlinedCircle mark="check" size={25} />;
    case 'unschedule':
      return <OutlinedCircle mark="cross" size={25} />;
    case 'save':
      return <Icon name={finished ? 'ui:saveOn' : 'ui:save'} size={25} />;
    case 'unsave':
      return <Icon name="ui:save" size={25} />;
  }
}

const MobileEventCard = memo(MobileEventCardBase);
export default MobileEventCard;
